import type { AnalysisSummaryResponse, VerdictLabel } from '../types/api';

/**
 * The reports this browser has opened, and the ones its owner chose to keep.
 *
 * <p>There are no accounts in this product, so "my properties" has to live somewhere the visitor
 * already is: their own browser. The list holds nothing that is not already on the report they
 * just read - the address, the score and the id - and it is the only way back to a report whose
 * link has been closed, because the access key that opens it is also only in this browser.
 *
 * <p>Every accessor tolerates storage being unavailable, because private windows and blocked site
 * data are normal rather than exceptional. Losing this list costs the shortcut, not the product.
 */

const KEY = 'scanhome.reports';

/** Beyond this, the list stops being a shortcut and starts being an archive nobody reads. */
const LIMIT = 24;

export interface RememberedReport {
  analysisId: string;
  addressLine: string;
  postcode: string;
  score: number | null;
  label: VerdictLabel | null;
  /** When this browser last opened it, ISO. */
  openedAt: string;
  /** True once the reader has explicitly kept it, which also exempts it from the trim below. */
  saved: boolean;
}

function readAll(): RememberedReport[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (entry): entry is RememberedReport =>
        typeof entry === 'object'
        && entry !== null
        && typeof (entry as RememberedReport).analysisId === 'string'
        && typeof (entry as RememberedReport).addressLine === 'string',
    );
  } catch {
    return [];
  }
}

function writeAll(entries: RememberedReport[]): void {
  try {
    // Kept ones are never trimmed; the rest are a most-recently-opened window.
    const kept = entries.filter((entry) => entry.saved);
    const rest = entries.filter((entry) => !entry.saved).slice(0, Math.max(0, LIMIT - kept.length));
    const merged = entries.filter((entry) => kept.includes(entry) || rest.includes(entry));
    window.localStorage.setItem(KEY, JSON.stringify(merged));
  } catch {
    // Nothing to do: the visit simply is not remembered.
  }
}

function sorted(entries: RememberedReport[]): RememberedReport[] {
  return [...entries].sort((a, b) => b.openedAt.localeCompare(a.openedAt));
}

/** Everything this browser remembers, newest first. */
export function rememberedReports(): RememberedReport[] {
  return sorted(readAll());
}

/** The ones opened recently and not kept, for the shortcut on the home page. */
export function recentReports(limit = 4): RememberedReport[] {
  return sorted(readAll()).slice(0, limit);
}

export function savedReports(): RememberedReport[] {
  return sorted(readAll().filter((entry) => entry.saved));
}

export function isSaved(analysisId: string): boolean {
  return readAll().some((entry) => entry.analysisId === analysisId && entry.saved);
}

/**
 * Records that a report was opened, updating the score if it has been unlocked since.
 *
 * <p>Called on every report view, so it must not lose the kept flag that a previous visit set.
 */
export function rememberVisit(summary: AnalysisSummaryResponse, score: number | null = null): void {
  const entries = readAll();
  const existing = entries.find((entry) => entry.analysisId === summary.analysisId);
  const next: RememberedReport = {
    analysisId: summary.analysisId,
    addressLine: summary.addressLine,
    postcode: summary.postcode,
    score: score ?? summary.overall.score ?? existing?.score ?? null,
    label: summary.overall.label ?? existing?.label ?? null,
    openedAt: new Date().toISOString(),
    saved: existing?.saved ?? false,
  };
  writeAll([next, ...entries.filter((entry) => entry.analysisId !== summary.analysisId)]);
}

/** Keeps a report, or stops keeping it. Returns the state it ended in. */
export function toggleSaved(analysisId: string): boolean {
  const entries = readAll();
  const existing = entries.find((entry) => entry.analysisId === analysisId);
  if (!existing) {
    return false;
  }
  const next = !existing.saved;
  writeAll(
    entries.map((entry) => (entry.analysisId === analysisId ? { ...entry, saved: next } : entry)),
  );
  return next;
}

/** Takes a report out of the list entirely. */
export function forgetReport(analysisId: string): void {
  writeAll(readAll().filter((entry) => entry.analysisId !== analysisId));
}

/**
 * Puts a removed report back, exactly as it was.
 *
 * <p>Removing is one press on a small control next to a list of addresses, which is precisely the
 * shape of an accidental press. Undo needs the whole entry rather than its id, because by the time
 * the reader changes their mind the entry is gone from storage - so the caller holds it.
 */
export function restoreReport(entry: RememberedReport): void {
  const entries = readAll().filter((existing) => existing.analysisId !== entry.analysisId);
  writeAll([entry, ...entries]);
}
